import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";

import { useMyOrganizations, usableOrganizationId, useSelectedOrganizationId } from "@/auth/organization";
import { Alert, Badge, Button, ErrorState, FullPageSpinner, Modal } from "@/components/ui";
import { session } from "@/lib/api";
import { ApiError, errorMessage } from "@/lib/http";
import { legal, outstanding, termsRequired, VERSION_NOT_CURRENT } from "@/lib/legal";
import { canAccept, canContinue, gateReducer, initialGate, type GateItem } from "@/lib/policy-gate";

import { PolicyViewer } from "./PolicyViewer";

/**
 * Keeps the signed-in app behind the mandatory policies. Nothing is mounted until the server says
 * nothing is outstanding (fail closed); afterwards the app stays mounted but inert under the gate
 * whenever something new is required, so no work in progress is lost.
 */
export function PolicyGate({ children }: { children: ReactNode }) {
  // Only a business this account really has and that is active: a remembered one may be another account's,
  // or one they left or that was suspended or deleted, which the portal refuses (PHP-3702). Without one the
  // account-level policies are checked, and once the switcher has picked a business the gate checks it too.
  const remembered = useSelectedOrganizationId();
  const organizations = useMyOrganizations();
  const orgId = usableOrganizationId(remembered, organizations.data?.items);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const key = ["legal", "requirements", orgId] as const;
  const requirements = useQuery({
    queryKey: key,
    queryFn: () => legal.requirements(orgId),
    staleTime: 60_000,
    enabled: !organizations.isPending,
  });
  const [state, dispatch] = useReducer(gateReducer, initialGate);
  const [everCleared, setEverCleared] = useState(false);
  /** A call was refused for missing acceptance and the fresh requirements haven't arrived yet. */
  const [awaiting, setAwaiting] = useState(false);
  const awaitingRef = useRef(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const inFlight = useRef(new Set<string>());
  const heading = useRef<HTMLHeadingElement>(null);

  const data = requirements.data;
  // Derived while rendering (not in an effect), so the app mounts in the same pass the answer arrives.
  if (data?.clear && !everCleared) setEverCleared(true);
  const blocked = awaiting || (data !== undefined && !data.clear);

  // Settles `awaiting` only once the requirements were really re-read: a failed reload keeps the gate up.
  const settle = useCallback(() => {
    const st = queryClient.getQueryState(["legal", "requirements", orgId]);
    if (st?.status === "success" && !st.isInvalidated) {
      awaitingRef.current = false;
      setAwaiting(false);
    }
  }, [queryClient, orgId]);

  useEffect(() => {
    return termsRequired.subscribe(() => {
      // Close at once; many calls failing together are one signal, so only the first reloads.
      setOpenId(null);
      if (awaitingRef.current) return;
      awaitingRef.current = true;
      setAwaiting(true);
      void queryClient.invalidateQueries({ queryKey: ["legal", "requirements"] }).then(settle);
    });
  }, [queryClient, settle]);

  useEffect(() => {
    if (!data) return;
    dispatch({ type: "load", requirements: data.clear ? [] : outstanding(data) });
  }, [data]);

  const reload = useCallback(async () => {
    await requirements.refetch();
    settle();
  }, [requirements, settle]);

  const gateShown = blocked;
  useEffect(() => {
    if (gateShown) heading.current?.focus();
  }, [gateShown]);

  const accept = async (item: GateItem) => {
    const id = item.requirement.version_id;
    if (inFlight.current.has(id) || !canAccept(item)) return;
    inFlight.current.add(id);
    dispatch({ type: "accepting", versionId: id });
    try {
      await legal.accept(id, orgId);
      dispatch({ type: "accepted", versionId: id });
    } catch (err) {
      if (err instanceof ApiError && err.reason === VERSION_NOT_CURRENT) {
        dispatch({ type: "superseded", documentId: item.requirement.document_id });
        void requirements.refetch();
      } else {
        dispatch({ type: "failed", versionId: id, message: errorMessage(err) });
      }
    } finally {
      inFlight.current.delete(id);
    }
  };

  const signOut = async () => {
    try {
      await session.signOut();
    } finally {
      queryClient.clear();
      navigate("/login", { replace: true });
    }
  };

  // Before the first clear answer nothing of the app is shown.
  if (!everCleared) {
    if (data === undefined) {
      if (organizations.isError)
        return <ErrorState error={organizations.error} onRetry={() => void organizations.refetch()} />;
      if (requirements.isError)
        return <ErrorState error={requirements.error} onRetry={() => void requirements.refetch()} />;
      return <FullPageSpinner />;
    }
  }

  const opened = state.items.find((i) => i.requirement.version_id === openId) ?? null;
  return (
    <>
      {/* Full height, so the app shell inside (h-full) still fills the window. */}
      {everCleared && (
        <div inert={blocked} className="h-full">
          {children}
        </div>
      )}
      {gateShown && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-canvas">
          <div className="mx-auto max-w-2xl space-y-4 px-4 py-8">
            <h1 ref={heading} tabIndex={-1} className="text-2xl font-semibold tracking-tight outline-none">
              Review and accept our policies
            </h1>
            <p className="text-sm text-fg-muted">
              Please open and read each policy, then accept it to continue.
            </p>
            {requirements.isError && <ErrorState error={requirements.error} onRetry={() => void reload()} />}
            <ul className="space-y-3">
              {state.items.map((item) => (
                <PolicyRow
                  key={item.requirement.version_id}
                  item={item}
                  onOpen={() => setOpenId(item.requirement.version_id)}
                  onAccept={() => void accept(item)}
                />
              ))}
            </ul>
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <Button variant="ghost" onClick={() => void signOut()}>
                Sign out
              </Button>
              <Button
                disabled={!(canContinue(state) || state.items.length === 0)}
                loading={requirements.isFetching}
                onClick={() => void reload()}
              >
                Continue
              </Button>
            </div>
          </div>
          <Modal
            open={opened !== null}
            onClose={() => setOpenId(null)}
            title={opened?.requirement.title ?? ""}
            size="lg"
          >
            {opened && (
              <PolicyViewer
                versionId={opened.requirement.version_id}
                orgId={orgId}
                headingLevel={2}
                onLoaded={() => dispatch({ type: "opened", versionId: opened.requirement.version_id })}
              />
            )}
          </Modal>
        </div>
      )}
    </>
  );
}

function PolicyRow({ item, onOpen, onAccept }: { item: GateItem; onOpen: () => void; onAccept: () => void }) {
  const r = item.requirement;
  return (
    <li className="space-y-3 rounded-xl border border-border bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 id={`policy-${r.version_id}`} className="text-base font-semibold">
            {r.title}
          </h2>
          <p className="text-xs text-fg-muted">
            v{r.version_number}
            {r.reason === "updated" ? " · Updated" : ""}
          </p>
        </div>
        {item.status === "accepted" && <Badge tone="success">Accepted</Badge>}
      </div>
      {r.description && <p className="text-sm text-fg-muted">{r.description}</p>}
      {r.change_summary && <p className="text-sm text-fg">{r.change_summary}</p>}
      {item.superseded && (
        <Alert tone="warning">
          {r.title} was updated while you were reading. Open and read v{r.version_number} before accepting.
        </Alert>
      )}
      {item.status === "failed" && (
        <Alert tone="danger">We couldn&apos;t record your acceptance. {item.error}</Alert>
      )}
      {item.status !== "accepted" && (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={onOpen} aria-describedby={`policy-${r.version_id}`}>
            Open and read
          </Button>
          {item.status === "failed" ? (
            <Button onClick={onAccept} aria-label={`Retry accepting ${r.title}`}>
              Retry
            </Button>
          ) : (
            <Button
              disabled={!canAccept(item)}
              loading={item.status === "accepting"}
              aria-busy={item.status === "accepting"}
              onClick={onAccept}
              aria-label={`I accept ${r.title}`}
            >
              I accept
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
